import { useSyncExternalStore } from 'react';

/**
 * Per-device collapsed/expanded state for server folders. A folder defaults
 * to collapsed (Discord parity); this set tracks the ids the user has
 * expanded. Pure UI state → localStorage, reactive via useSyncExternalStore
 * so the rail updates live when toggled in any tab.
 */

const STORAGE_KEY = 'bunker.guildFolderExpanded.v1';

function load(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? new Set(parsed.filter((x): x is string => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

let expanded = load();
const listeners = new Set<() => void>();

function emit() {
  // New Set identity so useSyncExternalStore's reference check fires.
  expanded = new Set(expanded);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...expanded]));
  } catch {
    /* private mode / quota — in-memory state still works for this session */
  }
  for (const l of listeners) l();
}

export function toggleFolderExpanded(folderId: string): void {
  if (expanded.has(folderId)) expanded.delete(folderId);
  else expanded.add(folderId);
  emit();
}

export function setFolderExpanded(folderId: string, value: boolean): void {
  if (value === expanded.has(folderId)) return;
  if (value) expanded.add(folderId);
  else expanded.delete(folderId);
  emit();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Reactive set of expanded folder ids. */
export function useExpandedFolders(): Set<string> {
  return useSyncExternalStore(subscribe, () => expanded, () => expanded);
}
